import { useState } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Upload, Download, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { apiRequest, authService } from '../utils/auth';

export default function ImportExport() {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const isAdmin = authService.isAdmin();

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rfq/template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to download template');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rfq_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.csv')) {
      toast.error('Please upload .xlsx or .csv file only');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiRequest('/api/rfq/import', {
        method: 'POST',
        body: formData,
      });

      setImportResult(response);
      
      if (response.success_count > 0) {
        toast.success(`Successfully imported ${response.success_count} RFQ records`);
      }
      
      if (response.error_count > 0) {
        toast.warning(`${response.error_count} records had errors`);
      }
    } catch (error) {
      toast.error(error.message || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleExport = async () => {
    if (!isAdmin) {
      toast.error('Admin access required for export');
      return;
    }

    setExporting(true);

    try {
      const blob = await apiRequest('/api/rfq/export', {
        method: 'GET',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rfq_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('RFQ data exported successfully');
    } catch (error) {
      toast.error(error.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div data-testid="import-export-page">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-[#0A2540]">Import / Export</h2>
          <p className="text-sm text-gray-600 mt-1">Bulk import RFQ data and export reports</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Import Section */}
          <Card className="p-6 border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Upload className="w-6 h-6 text-[#0A2540]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0A2540]">Import RFQ Data</h3>
                <p className="text-sm text-gray-600">Upload Excel or CSV file</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="file-upload" className="cursor-pointer">
                  <div className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#0A2540] transition-colors">
                    <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">
                      {importing ? 'Importing...' : 'Click to select file or drag & drop'}
                    </p>
                    <p className="text-xs text-gray-400">Supported formats: .xlsx, .csv</p>
                  </div>
                  <input
                    id="file-upload"
                    data-testid="file-import-input"
                    type="file"
                    accept=".xlsx,.csv"
                    onChange={handleImport}
                    disabled={importing}
                    className="hidden"
                  />
                </Label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-[#0A2540]">Required Fields:</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    disabled={downloadingTemplate}
                    className="text-xs"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    {downloadingTemplate ? 'Downloading...' : 'Download Template'}
                  </Button>
                </div>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• customer_code (TAC-XXX format)</li>
                  <li>• part_no, part_name, part_description</li>
                  <li>• rfq_qty (number)</li>
                  <li>• category (comma-separated values)</li>
                  <li>• rfq_date (YYYY-MM-DD format)</li>
                  <li>• quotation_date, quoted_price (optional)</li>
                  <li>• final_price, purchase_order_number (optional)</li>
                  <li>• po_qty, po_value (optional)</li>
                </ul>
              </div>

              {importResult && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-sm text-green-800">Success</span>
                    <span className="text-sm font-semibold text-green-800">
                      {importResult.success_count}
                    </span>
                  </div>
                  
                  {importResult.error_count > 0 && (
                    <>
                      <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                        <span className="text-sm text-red-800">Errors</span>
                        <span className="text-sm font-semibold text-red-800">
                          {importResult.error_count}
                        </span>
                      </div>
                      
                      {importResult.errors.length > 0 && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg max-h-40 overflow-y-auto">
                          <div className="flex items-start gap-2 mb-2">
                            <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                            <h5 className="text-sm font-semibold text-yellow-800">Error Details:</h5>
                          </div>
                          <ul className="text-xs text-yellow-700 space-y-1">
                            {importResult.errors.map((error, index) => (
                              <li key={index}>• {error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Export Section */}
          <Card className="p-6 border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Download className="w-6 h-6 text-[#0A2540]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0A2540]">Export RFQ Data</h3>
                <p className="text-sm text-gray-600">Download all RFQ records as CSV</p>
              </div>
            </div>

            <div className="space-y-4">
              {!isAdmin && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Admin access required for export
                  </p>
                </div>
              )}

              <Button
                data-testid="export-button"
                onClick={handleExport}
                disabled={exporting || !isAdmin}
                className="w-full bg-[#0A2540] hover:bg-[#0d2f52] text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export RFQ Data as CSV'}
              </Button>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-[#0A2540] mb-2">Export Includes:</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• All RFQ records</li>
                  <li>• Customer details</li>
                  <li>• Part information</li>
                  <li>• Pricing and business calculations</li>
                  <li>• Status and dates</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
